/**
 * Small toggle primitives for the Admin area (per-user roles/overrides and the
 * role permission-grant matrix). The portal has no `ui/checkbox` primitive, so
 * these compose the existing tokens (border/primary/muted) into an accessible
 * checkbox using a native `<input type="checkbox">` styled via `accent-color`
 * (the black `--primary` accent) — no new styling system, no new dependency.
 */
import * as React from "react";

import { cn } from "@/lib/utils";

/** A single labelled checkbox row used by the matrix + override pickers. */
export function CheckRow({
  checked,
  onCheckedChange,
  disabled,
  label,
  hint,
}: {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  disabled?: boolean;
  label: React.ReactNode;
  hint?: React.ReactNode;
}) {
  return (
    <label
      className={cn(
        "flex items-start gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors",
        disabled
          ? "cursor-not-allowed text-muted-foreground"
          : "cursor-pointer hover:bg-accent",
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onCheckedChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
      />
      <span className="flex min-w-0 flex-col">
        <span className="truncate">{label}</span>
        {hint ? (
          <span className="truncate text-xs text-muted-foreground">{hint}</span>
        ) : null}
      </span>
    </label>
  );
}
